import { useState, useEffect, useCallback } from 'react';
import companyService from '../services/CompanyService';
import useApi from './useApi';

const useCompany = () => {
  // State management
  const [companies, setCompanies] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  // Initialize API hook
  const api = useApi();

  // Initialize company service on mount
  useEffect(() => {
    companyService.initialize(api);
    console.log('[useCompany] CompanyService initialized');
  }, [api]);

  // Utility functions
  const logInfo = (message, data = null) => {
    console.log(`[useCompany] ${message}`, data || '');
  };

  const logError = (message, error = null) => {
    console.error(`[useCompany] ERROR: ${message}`, error || '');
  };

  const setErrorState = (error) => {
    const errorMessage = error?.message || error || 'An unknown error occurred';
    setError(errorMessage);
    logError('Setting error state', errorMessage);
  };

  const clearError = () => {
    setError(null);
  };

  // Load companies list
  const loadCompanies = useCallback(async () => {
    logInfo('Loading companies list');
    setLoading(true);
    clearError();

    try {
      const companiesList = await companyService.listCompanies();
      setCompanies(companiesList);
      logInfo('Companies loaded successfully', { count: companiesList.length });

      // Set first company as current if none selected
      if (companiesList.length > 0 && !currentCompany) {
        setCurrentCompany(companiesList[0]);
        logInfo('Set first company as current', companiesList[0]);
      }

      return companiesList;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [currentCompany]);

  // Create new company
  const createCompany = useCallback(async (companyData) => {
    logInfo('Creating new company', companyData);
    setLoading(true);
    clearError();

    try {
      const newCompany = await companyService.createCompany(companyData);
      
      // Update companies list
      setCompanies(prev => [...prev, newCompany.company || newCompany]);
      
      // Set as current company
      setCurrentCompany(newCompany.company || newCompany);
      
      logInfo('Company created successfully', newCompany);
      return newCompany;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get company details
  const getCompanyDetails = useCallback(async (companyId) => {
    logInfo('Getting company details', { companyId });
    setLoading(true);
    clearError();

    try {
      const company = await companyService.getCompany(companyId);
      logInfo('Company details loaded', company);
      return company;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update company
  const updateCompany = useCallback(async (companyId, updateData) => {
    logInfo('Updating company', { companyId, updateData });
    setLoading(true);
    clearError();

    try {
      const response = await companyService.updateCompany(companyId, updateData);
      const updatedCompany = response.company || response; // Handle both response formats
      
      // Update in companies list
      setCompanies(prev => 
        prev.map(company => 
          company.id === companyId ? { ...company, ...updatedCompany } : company
        )
      );

      // Update current company if it's the one being updated
      if (currentCompany && currentCompany.id === companyId) {
        setCurrentCompany(prev => ({ ...prev, ...updatedCompany }));
      }

      logInfo('Company updated successfully', response);
      return response;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [currentCompany]);

  // Update company with files
  const updateCompanyWithFiles = useCallback(async (companyId, textData, fileData = {}) => {
    logInfo('Updating company with files', { companyId, textData, fileData });
    setLoading(true);
    clearError();

    try {
      const response = await companyService.updateCompanyWithFiles(companyId, textData, fileData);
      const updatedCompany = response.company || response; // Handle both response formats
      
      // Update in companies list
      setCompanies(prev => 
        prev.map(company => 
          company.id === companyId ? { ...company, ...updatedCompany } : company
        )
      );

      // Update current company if it's the one being updated
      if (currentCompany && currentCompany.id === companyId) {
        setCurrentCompany(prev => ({ ...prev, ...updatedCompany }));
      }

      logInfo('Company updated successfully with files', response);
      return response;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [currentCompany]);

  // Upload document
  const uploadDocument = useCallback(async (companyId, file, fileType = '') => {
    logInfo('Uploading document', { companyId, fileName: file.name, fileType });
    setUploading(true);
    clearError();

    try {
      const uploadResult = await companyService.uploadDocument(companyId, file, fileType);
      logInfo('Document uploaded successfully', uploadResult);
      return uploadResult;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setUploading(false);
    }
  }, []);

  // Upload multiple documents
  const uploadMultipleDocuments = useCallback(async (companyId, files, fileType = '') => {
    logInfo('Uploading multiple documents', { companyId, fileCount: files.length, fileType });
    setUploading(true);
    clearError();

    try {
      const uploadResults = await companyService.uploadMultipleDocuments(companyId, files, fileType);
      logInfo('Multiple documents upload completed', uploadResults);
      return uploadResults;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setUploading(false);
    }
  }, []);

  // Analyze company
  const analyzeCompany = useCallback(async (companyId, analysisOptions = {}) => {
    logInfo('Starting company analysis', { companyId, analysisOptions });
    setAnalyzing(true);
    clearError();

    try {
      const analysisResult = await companyService.analyzeCompany(companyId, analysisOptions);
      logInfo('Company analysis completed', analysisResult);
      return analysisResult;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setAnalyzing(false);
    }
  }, []);

  // Analyze company materials using new AI endpoint
  const analyzeCompanyMaterials = useCallback(async (companyId, analysisOptions = {}) => {
    logInfo('Analyzing company materials', { companyId, analysisOptions });
    setAnalyzing(true);
    clearError();

    try {
      const analysisResult = await companyService.analyzeCompanyMaterials(companyId, analysisOptions);
      
      // Update current company with analysis results
      if (analysisResult.company) {
        setCurrentCompany(analysisResult.company);
        
        // Update company in the companies list
        setCompanies(prev => prev.map(company => 
          company.id === companyId ? analysisResult.company : company
        ));
      }
      
      logInfo('Company materials analysis completed', analysisResult);
      return analysisResult;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setAnalyzing(false);
    }
  }, []);

  // Analyze from website (legacy method)
  const analyzeFromWebsite = useCallback(async (websiteUrl, analysisOptions = {}) => {
    logInfo('Analyzing company from website', { websiteUrl, analysisOptions });
    setAnalyzing(true);
    clearError();

    try {
      const analysisResult = await companyService.analyzeFromWebsite(websiteUrl, analysisOptions);
      
      // Add the new company to the list
      const newCompany = analysisResult.company;
      setCompanies(prev => [...prev, newCompany]);
      setCurrentCompany(newCompany);
      
      logInfo('Website analysis completed', analysisResult);
      return analysisResult;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setAnalyzing(false);
    }
  }, []);

  // Delete company
  const deleteCompany = useCallback(async (companyId) => {
    logInfo('Deleting company', { companyId });
    setLoading(true);
    clearError();

    try {
      await companyService.deleteCompany(companyId);
      
      // Remove from companies list
      setCompanies(prev => prev.filter(company => company.id !== companyId));
      
      // Clear current company if it was deleted
      if (currentCompany && currentCompany.id === companyId) {
        setCurrentCompany(null);
      }

      logInfo('Company deleted successfully');
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [currentCompany]);

  // Switch current company
  const switchCompany = useCallback((company) => {
    logInfo('Switching current company', company);
    setCurrentCompany(company);
  }, []);

  // Get analysis progress
  const getAnalysisProgress = useCallback(async (companyId, requestId) => {
    logInfo('Getting analysis progress', { companyId, requestId });

    try {
      return await companyService.getAnalysisProgress(companyId, requestId);
    } catch (error) {
      logError('Failed to get analysis progress', error);
      throw error;
    }
  }, []);

  // Get analysis result
  const getAnalysisResult = useCallback(async (companyId, requestId) => {
    logInfo('Getting analysis result', { companyId, requestId });

    try {
      return await companyService.getAnalysisResult(companyId, requestId);
    } catch (error) {
      logError('Failed to get analysis result', error);
      throw error;
    }
  }, []);

  // Load companies on mount
  useEffect(() => {
    loadCompanies().catch(error => {
      logError('Failed to load companies on mount', error);
    });
  }, [loadCompanies]);

  return {
    // State
    companies,
    currentCompany,
    loading,
    error,
    uploading,
    analyzing,

    // Actions
    loadCompanies,
    createCompany,
    getCompanyDetails,
    updateCompany,
    updateCompanyWithFiles,
    uploadDocument,
    uploadMultipleDocuments,
    analyzeCompany,
    analyzeCompanyMaterials,
    analyzeFromWebsite,
    getAnalysisProgress,
    getAnalysisResult,
    deleteCompany,
    switchCompany,
    clearError,

    // Computed state
    hasCompanies: companies.length > 0,
    hasCurrentCompany: !!currentCompany,
    isProcessing: loading || uploading || analyzing
  };
};

export default useCompany; 